/** Stage labels + helpers shared across pages. */
export const STAGES = [
    { key: "ceo_briefing", label: "Briefing" },
    { key: "competitor_analysis", label: "Competitor" },
    { key: "brand_dna", label: "Brand DNA" },
    { key: "research", label: "Research" },
    { key: "business_plan", label: "Business Plan" },
    { key: "concepts", label: "Concepts" },
    { key: "script", label: "Script" },
    { key: "storyboard", label: "Storyboard" },
    { key: "asset_upload", label: "Reference Lock" },
    { key: "presentation", label: "Presentation" },
    { key: "prompts", label: "Prompt Pack" },
    { key: "qc_review", label: "QC Review" },
    { key: "post_production", label: "Post-Production" },
    { key: "final_delivery", label: "Final Delivery" },
];

export const AGENTS = [
    { key: "ceo", label: "CEO" },
    { key: "competitor_analyst", label: "Competitor Analyst", stage: "competitor_analysis" },
    { key: "brand_analyst", label: "Brand Analyst", stage: "brand_dna" },
    { key: "research", label: "Research", stage: "research" },
    { key: "business_plan", label: "Brand Strategist", stage: "business_plan" },
    { key: "creative_director", label: "Creative Director", stage: "concepts" },
    { key: "script", label: "Script", stage: "script" },
    { key: "storyboard", label: "Storyboard", stage: "storyboard" },
    { key: "presentation", label: "Presentation", stage: "presentation" },
    { key: "prompt_engineer", label: "Prompt Engineer", stage: "prompts" },
    { key: "qc", label: "QC" },
    { key: "post_production", label: "Post-Production", stage: "post_production" },
];

/** Fixed asset usage tags surfaced as multi-select chips. */
export const ASSET_USAGE_TAGS = [
    { value: "logo_image", label: "Logo Image" },
    { value: "logo_animation", label: "Logo Animation" },
    { value: "brand_intro_video", label: "Brand Intro Video" },
    { value: "brand_outro_video", label: "Brand Outro Video" },
    { value: "previous_ads", label: "Previous Ads" },
    { value: "reference_image", label: "Reference Image" },
    { value: "reference_video", label: "Reference Video" },
    { value: "brand_guidelines", label: "Brand Guidelines" },
    { value: "cta_reference", label: "CTA Reference" },
    { value: "product_visual", label: "Product Visual" },
    { value: "voiceover_sample", label: "Voiceover Sample" },
];

export const PROJECT_TYPES = [
    { value: "brand_promotion_video", label: "Brand Promotion Video" },
    { value: "social_media_ad", label: "Social Media Ad" },
    { value: "event_promotion", label: "Event Promotion" },
    { value: "product_launch", label: "Product Launch" },
    { value: "real_estate_campaign", label: "Real Estate Campaign" },
    { value: "music_video_concept", label: "Music Video Concept" },
    { value: "corporate_video", label: "Corporate Video" },
    { value: "explainer_video", label: "Explainer Video" },
    { value: "client_pitch_deck", label: "Client Pitch Deck" },
    { value: "monthly_content_strategy", label: "Monthly Content Strategy" },
];

export const LANGUAGES = [
    { value: "english", label: "English" },
    { value: "hindi", label: "Hindi" },
    { value: "gujarati", label: "Gujarati" },
    { value: "hinglish", label: "Hinglish" },
    { value: "roman_gujarati", label: "Roman Gujarati" },
];

/* ─────────────────────────────────────────────────────────────
 * UX Sprint 1 — User-facing 8-stage timeline.
 *
 * Frontend-only grouping over the existing backend stage keys
 * (`STAGES` above + the legacy `TABS` array on ProjectBoard).
 * Backend stage keys + APIs are NEVER renamed.
 *
 * Each user stage declares:
 *   - key, label, hint (short next-action sentence)
 *   - primaryTab : the existing tab key the timeline routes to
 *   - subTabs    : ordered list of {key,label} sub-pills shown
 *                  under the timeline when this user-stage is active.
 *                  Each `key` is an existing tab key (deep-link safe).
 *   - statusFrom : ordered list of backend stage keys whose statuses
 *                  contribute to this user-stage's derived status.
 *                  All listed stages must be `approved` for the
 *                  user-stage to be "Approved".
 * ───────────────────────────────────────────────────────────── */
export const USER_STAGES = [
    {
        key: "brief",
        label: "Brief",
        hint: "Lock the client brief and kick off strategy.",
        primaryTab: "tasks",
        subTabs: [
            { key: "tasks", label: "Brief & Run Agents" },
        ],
        statusFrom: ["ceo_briefing"],
    },
    {
        key: "asset_upload",
        label: "Asset Upload",
        hint: "Upload reference assets or skip with reason.",
        primaryTab: "assets",
        subTabs: [
            { key: "assets", label: "Assets & References" },
        ],
        statusFrom: ["asset_upload"],
    },
    {
        key: "brand_understanding",
        label: "Brand Understanding",
        hint: "Approve the Brand DNA before strategy.",
        primaryTab: "brand_dna",
        subTabs: [
            { key: "brand_dna", label: "Brand DNA" },
        ],
        statusFrom: ["brand_dna"],
    },
    {
        key: "market_research",
        label: "Market Research",
        hint: "Approve competitor + market research.",
        primaryTab: "research",
        subTabs: [
            { key: "competitor", label: "Competitor" },
            { key: "research", label: "Research" },
        ],
        statusFrom: ["competitor_analysis", "research"],
    },
    {
        key: "campaign_strategy",
        label: "Campaign Strategy",
        hint: "Lock the business plan, social plan, and channels.",
        primaryTab: "_campaign_overview",
        subTabs: [
            { key: "_campaign_overview", label: "Overview" },
            { key: "business_plan", label: "Business Plan" },
            { key: "strategy", label: "Strategy Approval" },
            { key: "social_strategy", label: "Social Content Plan" },
            { key: "distribution", label: "Distribution Channels" },
            { key: "influencers", label: "Influencer Ideas" },
        ],
        statusFrom: ["business_plan"],
    },
    {
        key: "creative_concepts",
        label: "Creative Concepts",
        hint: "Pick a concept, then approve a script variant.",
        primaryTab: "concepts",
        subTabs: [
            { key: "concepts", label: "Concepts" },
            { key: "script", label: "Script" },
        ],
        statusFrom: ["concepts", "script"],
    },
    {
        key: "script_storyboard",
        label: "Script & Storyboard",
        hint: "Lock the storyboard and reference assets.",
        primaryTab: "storyboard",
        subTabs: [
            { key: "storyboard", label: "Storyboard" },
        ],
        statusFrom: ["storyboard"],
    },
    {
        key: "visual_bible",
        label: "Visual Bible",
        hint: "Approve characters, environments, and reference lock.",
        primaryTab: "_visual_bible_overview",
        subTabs: [
            { key: "_visual_bible_overview", label: "Overview" },
            { key: "storyboard", label: "Production Continuity" },
            { key: "assets", label: "Reference Lock" },
        ],
        // Workflow Hardening 2 / FIX 8 — asset_upload is its own workflow
        // card (before Competitor Analysis). Visual Bible no longer derives
        // its status from asset_upload (skipping assets used to mislabel
        // the tile "Visual Bible — SKIPPED").
        statusFrom: [],
    },
    {
        key: "delivery",
        label: "Presentation & Delivery",
        hint: "Generate prompts, deck, and final delivery.",
        primaryTab: "_delivery_overview",
        subTabs: [
            { key: "_delivery_overview", label: "Overview" },
            { key: "presentation", label: "Presentation" },
            { key: "video_prompts", label: "Video Prompts" },
            { key: "video_studio", label: "Video Studio" },
            { key: "delivery", label: "Final Delivery" },
            { key: "share", label: "Share Link" },
        ],
        statusFrom: ["presentation", "final_delivery"],
    },
];

/** UX Sprint 2 — per-user-stage metadata for the workspace shell.
 *  Purpose text shown under stage hero; primary CTA spec; assistant hint. */
export const STAGE_META = {
    brief: {
        purpose: "Define the goal, audience, and scope. Everything downstream is built from this brief.",
        primary: { label: "Start Auto Pipeline", target: "auto-start-btn", kind: "scroll" },
        assistantHint: "Lock the brief, then start the Auto Pipeline so the agents can build strategy.",
    },
    brand_understanding: {
        purpose: "Approve the Brand DNA — voice, audience, and trust points — before any creative work.",
        primary: { label: "Open Brand DNA", target: "brand_dna", kind: "tab" },
        assistantHint: "Review the Brand DNA output and approve it to unlock Market Research.",
    },
    market_research: {
        purpose: "Approve competitor analysis and research. These signals power campaign strategy.",
        primary: { label: "Open Research", target: "research", kind: "tab" },
        assistantHint: "Run or review Research, then approve both Competitor + Research to continue.",
    },
    campaign_strategy: {
        purpose: "Lock the business plan, social content plan, distribution channels, and influencer ideas.",
        primary: { label: "Open Business Plan", target: "business_plan", kind: "tab" },
        assistantHint: "Approve the business plan first, then refine social, distribution, and influencers.",
    },
    creative_concepts: {
        purpose: "Pick a concept direction and approve a script variant.",
        primary: { label: "Open Concepts", target: "concepts", kind: "tab" },
        assistantHint: "Choose a concept, then review the generated script before storyboard.",
    },
    script_storyboard: {
        purpose: "Lock the storyboard sequence — shot order, beats, and reference frames.",
        primary: { label: "Open Storyboard", target: "storyboard", kind: "tab" },
        assistantHint: "Approve the storyboard, then move to Visual Bible for character + environment locks.",
    },
    visual_bible: {
        purpose: "Approve characters, environments, and the visual reference lock. Required for downstream video.",
        primary: { label: "Open Production Continuity", target: "storyboard", kind: "tab" },
        assistantHint: "Generate or upload reference images for each character and environment, then approve.",
    },
    delivery: {
        purpose: "Generate the client deck, video prompt pack, and final delivery artifacts.",
        primary: { label: "Open Presentation", target: "presentation", kind: "tab" },
        assistantHint: "Generate the presentation, export the client PDF, and share the review link.",
    },
};

/** Existing tab keys that are NOT part of the primary timeline.
 *  These remain reachable through the "More Tools" drawer + deep links. */
export const ADVANCED_TAB_KEYS = [
    "tasks",            // Activity Log (also primary tab for "brief")
    "strategy",         // Strategy Approval raw (also sub-tab of campaign_strategy)
    "video_workflow",   // Advanced Shot Planning
    "prompts",          // Video Prompts Library
    "qc",               // Quality Check
    "post",             // Delivery Tools
    "share",            // Share Link (also sub-tab of delivery)
];

/** Reverse index: given any existing tab key, find which user-stage
 *  contains it. Used to make `/projects/:id?tab=storyboard` deep-link
 *  resolve to the correct timeline tile on load. */
export function userStageForTab(tabKey) {
    if (!tabKey) return null;
    for (const us of USER_STAGES) {
        if (us.primaryTab === tabKey) return us;
        if (us.subTabs.some((s) => s.key === tabKey)) return us;
    }
    return null;
}

/** Normalize a single backend stage's raw status (+ optional output blob)
 *  into one of the five user-facing status codes the timeline understands.
 *
 *  Mapping rules (Pipeline State Machine contract — Phase O.X universal):
 *   - approved                          → approved
 *   - skipped                           → approved   (label override: "Skipped")
 *   - failed                            → failed
 *   - processing/running/in_progress/leased → in_progress
 *   - pending/completed/manually_completed/changes_requested/rejected → needs_review
 *   - blocked_missing_inputs            → needs_review (label override: "Awaiting Inputs")
 *   - awaiting_user + output present    → approved
 *   - awaiting_user + no output         → not_started
 *   - everything else / missing         → not_started
 *
 *  Pure function — never reads from network or local storage. */
function _normalizeStageStatus(stageDict) {
    const raw = ((stageDict && stageDict.status) || "").toLowerCase();
    const out = stageDict && stageDict.output;
    const hasOutput = !!out && (typeof out !== "object" || (Array.isArray(out) ? out.length > 0 : Object.keys(out).length > 0));
    if (raw === "approved") return "approved";
    if (raw === "skipped") return "approved";
    if (raw === "failed") return "failed";
    if (raw === "processing" || raw === "running" || raw === "in_progress" || raw === "leased") return "in_progress";
    if (raw === "blocked_missing_inputs") return "needs_review";
    if (raw === "pending" || raw === "completed" || raw === "manually_completed") return "needs_review";
    if (raw === "changes_requested" || raw === "rejected") return "needs_review";
    if (raw === "awaiting_user") return hasOutput ? "approved" : "not_started";
    return "not_started";
}

/** Derive a user-facing status pill for a USER_STAGES entry.
 *  Returns one of: not_started | in_progress | needs_review | approved | failed.
 *  Pure function — testable + deterministic. */
export function userStageStatus(userStage, projectStages) {
    const stages = projectStages || {};
    const keys = userStage.statusFrom || [];
    if (keys.length === 0) return "not_started";
    const normalized = keys.map((k) => _normalizeStageStatus(stages[k]));
    if (normalized.some((s) => s === "failed")) return "failed";
    if (normalized.some((s) => s === "in_progress")) return "in_progress";
    if (normalized.some((s) => s === "needs_review")) return "needs_review";
    if (normalized.every((s) => s === "approved")) return "approved";
    return "not_started";
}

export function userStageStatusColor(status) {
    switch (status) {
        case "approved":     return "text-emerald-300 border-emerald-500/40 bg-emerald-500/5";
        case "in_progress":  return "text-amber-300 border-amber-500/40 bg-amber-500/5";
        case "needs_review": return "text-sky-300 border-sky-500/40 bg-sky-500/5";
        case "failed":       return "text-red-300 border-red-500/40 bg-red-500/5";
        default:             return "text-zinc-500 border-zinc-800 bg-zinc-950";
    }
}

export function userStageStatusLabel(status, userStage, projectStages) {
    // Optional override path: when caller passes userStage + projectStages
    // we can surface a more specific label for two special backend states
    // that share an aggregate bucket with another state:
    //   - blocked_missing_inputs → needs_review bucket, label "Awaiting Inputs"
    //   - skipped                → approved bucket,     label "Skipped"
    if (userStage && projectStages) {
        const keys = userStage.statusFrom || [];
        const raws = keys.map((k) => ((projectStages[k]?.status) || "").toLowerCase());
        if (status === "needs_review" && raws.some((s) => s === "blocked_missing_inputs")) {
            return "Awaiting Inputs";
        }
        if (status === "approved" && raws.length > 0 && raws.every((s) => s === "skipped")) {
            return "Skipped";
        }
    }
    switch (status) {
        case "approved":     return "Approved";
        case "in_progress":  return "In Progress";
        case "needs_review": return "Needs Review";
        case "failed":       return "Failed";
        default:             return "Not Started";
    }
}

export function stageStatusColor(status) {
    switch (status) {
        case "approved": return "text-emerald-400 border-emerald-500/30 bg-emerald-500/10";
        case "rejected": return "text-red-400 border-red-500/30 bg-red-500/10";
        case "failed": return "text-red-400 border-red-500/30 bg-red-500/10";
        case "changes_requested": return "text-amber-400 border-amber-500/30 bg-amber-500/10";
        case "pending": return "text-zinc-300 border-zinc-700 bg-zinc-900";
        case "in_progress": return "text-amber-400 border-amber-500/30 bg-amber-500/10";
        case "processing": return "text-amber-400 border-amber-500/30 bg-amber-500/10";
        default: return "text-zinc-500 border-zinc-800 bg-black";
    }
}
